import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Card, Loader } from "@/components/ui/Primitives";
import { Send, Mail, CheckCircle2, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

/**
 * TenantLetters — pre-written email letters landlords send to tenants.
 *
 * Flow: pick a template → optional lease → preview (auto-filled variables) →
 * edit subject/body if you want → click "Send via Resend" → log to
 * tenant_letters in Mongo. Recent sends shown at the bottom.
 */
export default function TenantLetters() {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null); // template_key
  const [leases, setLeases] = useState([]);
  const [leaseId, setLeaseId] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState([]);

  useEffect(() => {
    api.get("/letters/templates").then((r) => setTemplates(r.data?.templates || []))
       .catch(() => toast.error("Couldn't load letter templates"));
    api.get("/leases").then((r) => {
      const items = Array.isArray(r.data) ? r.data : (r.data?.leases || r.data?.items || []);
      setLeases(items);
    }).catch(() => {});
    refreshSent();
  }, []);

  const refreshSent = () => {
    api.get("/letters/sent").then((r) => setSent(r.data?.items || [])).catch(() => {});
  };

  const renderPreview = async (tkey, lid) => {
    setBusy(true);
    try {
      const { data } = await api.post("/letters/preview", { template_key: tkey, lease_id: lid || null });
      setPreview(data);
    } catch {
      toast.error("Couldn't render preview");
    } finally { setBusy(false); }
  };

  const pick = (tkey) => {
    setSelected(tkey);
    setPreview(null);
    renderPreview(tkey, leaseId);
  };

  const onLeaseChange = (lid) => {
    setLeaseId(lid);
    if (selected) renderPreview(selected, lid);
  };

  const sendNow = async () => {
    if (!preview?.to) { toast.error("No tenant email — enter one in the To field or attach a lease"); return; }
    setBusy(true);
    try {
      await api.post("/letters/send", {
        template_key: selected,
        lease_id: leaseId || null,
        variables: preview.variables_used,
        to_email: preview.to,
        subject: preview.subject,
        body: preview.body,
      });
      toast.success("Sent — tenant has it.");
      setSelected(null);
      setPreview(null);
      refreshSent();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="tenant-letters-page">
      <PageHeader
        title="Tenant Letters"
        subtitle="Pre-written templates — late rent, renewal offers, maintenance notices, rent increases. One-tap send via your branded Resend address."
      />

      {!selected ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="tenant-letters-grid">
          {templates.map((t) => (
            <button key={t.key} onClick={() => pick(t.key)} data-testid={`letter-template-${t.key}`}
              className="text-left bg-white rounded-xl border border-slate-200 p-5 hover:border-emerald-300 hover:shadow-sm transition-all">
              <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-emerald-700">{t.category}</div>
              <div className="font-display text-base font-bold text-slate-900 mt-1">{t.name}</div>
              <div className="text-xs text-slate-500 mt-2 line-clamp-3">{t.body.split("\n\n")[0]}…</div>
              <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-emerald-700">
                Use this template <Send className="w-3 h-3" />
              </div>
            </button>
          ))}
        </div>
      ) : (
        <div data-testid="tenant-letters-composer">
          <button onClick={() => { setSelected(null); setPreview(null); }} data-testid="letters-back"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-900 mb-3">
            <ArrowLeft className="w-4 h-4" /> Back to templates
          </button>

          <Card className="p-6">
            <div className="mb-4">
              <label className="block text-xs uppercase tracking-wider font-bold text-slate-500 mb-1.5">Attach to lease (optional)</label>
              <select value={leaseId} onChange={(e) => onLeaseChange(e.target.value)} data-testid="letters-lease-select"
                className="w-full px-3 py-2 rounded-md border border-slate-200 text-sm bg-white">
                <option value="">— None (manually enter email below) —</option>
                {leases.map((l) => (
                  <option key={l.lease_id || l.id} value={l.lease_id || l.id}>
                    {(l.tenant_name || l.tenant_email || "Tenant")} · {l.property_name || l.address || "Property"}
                  </option>
                ))}
              </select>
            </div>

            {busy && !preview ? <Loader /> : preview ? (
              <div className="space-y-3">
                <div>
                  <label className="block text-xs uppercase tracking-wider font-bold text-slate-500 mb-1">To</label>
                  <input value={preview.to || ""} onChange={(e) => setPreview({ ...preview, to: e.target.value })}
                    data-testid="letters-to-input" placeholder="tenant@example.com"
                    className="w-full px-3 py-2 rounded-md border border-slate-200 text-sm" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider font-bold text-slate-500 mb-1">Subject</label>
                  <input value={preview.subject} onChange={(e) => setPreview({ ...preview, subject: e.target.value })}
                    data-testid="letters-subject-input"
                    className="w-full px-3 py-2 rounded-md border border-slate-200 text-sm" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider font-bold text-slate-500 mb-1">Message</label>
                  <textarea value={preview.body} onChange={(e) => setPreview({ ...preview, body: e.target.value })}
                    data-testid="letters-body-input" rows={12}
                    className="w-full px-3 py-2 rounded-md border border-slate-200 text-sm font-mono leading-relaxed" />
                </div>
                <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                  <div className="text-xs text-slate-500">Sends via your Resend account · they reply to your real email.</div>
                  <button onClick={sendNow} disabled={busy || !preview.to} data-testid="letters-send-now"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-emerald-600 text-white font-semibold text-sm hover:bg-emerald-700 disabled:opacity-60">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    {busy ? "Sending…" : "Send via Resend"}
                  </button>
                </div>
              </div>
            ) : null}
          </Card>
        </div>
      )}

      {sent.length > 0 && (
        <div className="mt-8" data-testid="tenant-letters-sent">
          <h3 className="font-display text-sm font-bold text-slate-700 uppercase tracking-[0.16em] mb-3">Recently sent</h3>
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr><th className="text-left px-4 py-2 font-semibold">Date</th><th className="text-left px-4 py-2 font-semibold">To</th><th className="text-left px-4 py-2 font-semibold">Subject</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {sent.map((s) => (
                  <tr key={s.id}>
                    <td className="px-4 py-2 text-xs text-slate-500 tabular-nums">{(s.sent_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="px-4 py-2 text-slate-700"><Mail className="inline w-3 h-3 mr-1" />{s.tenant_email}</td>
                    <td className="px-4 py-2 text-slate-900 truncate max-w-[300px]"><CheckCircle2 className="inline w-3 h-3 mr-1 text-emerald-600" />{s.subject}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
